import { useInfiniteQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useMemo } from 'react';
import { tpoGenUrls } from 'config/urls';
import { PAGE_LIMIT, USE_DUMMY_METRIC_DATA } from '../../config/metricConfig';
import { generateDummyPromoMetricResponse } from '../../utils/dummyData';
import type {
    FiltersPayload,
    KpiViewType,
    MetricType,
    PromoMetricRequest,
    PromoMetricResponse,
    PromoMetricRow,
    SortOrder,
    TimePeriod,
} from '../../types';

interface UsePromoMetricDataParams {
    planId: string;
    dimensions: string[];
    kpis: string[];
    kpiViewType: KpiViewType;
    metricType: MetricType;
    timePeriod: TimePeriod;
    searchKeyword: string;
    sharedFilters: FiltersPayload;
    sortBy: string;
    sortOrder: SortOrder;
}

interface UsePromoMetricDataResult {
    dataSource: PromoMetricRow[];
    isPending: boolean;
    isFetching: boolean;
    isFetchingNextPage: boolean;
    hasNextPage: boolean;
    fetchNextPage: () => void;
    totalCount: number;
    loadedCount: number;
    error: unknown;
}

const fetchPromoMetricData = async (
    planId: string,
    body: PromoMetricRequest,
    signal: AbortSignal,
): Promise<PromoMetricResponse> => {
    if (USE_DUMMY_METRIC_DATA) {
        await new Promise((resolve) => setTimeout(resolve, 250));
        return generateDummyPromoMetricResponse(body);
    }
    let token = '';
    try {
        token = localStorage.getItem('accessToken') ?? '';
    } catch {
        // incognito / disabled storage; fall through with empty token
    }
    const response = await axios(tpoGenUrls.getPromoMetricReportData(planId), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        signal,
        data: body,
    });
    return response.data;
};

const usePromoMetricData = (params: UsePromoMetricDataParams): UsePromoMetricDataResult => {
    const {
        planId,
        dimensions,
        kpis,
        kpiViewType,
        metricType,
        timePeriod,
        searchKeyword,
        sharedFilters,
        sortBy,
        sortOrder,
    } = params;

    const enabled = !!planId && dimensions.length > 0 && kpis.length > 0;

    const { data, isPending, isFetching, isFetchingNextPage, fetchNextPage, hasNextPage, error } = useInfiniteQuery({
        queryKey: [
            'promoMetricData',
            planId,
            dimensions,
            kpis,
            kpiViewType,
            metricType,
            timePeriod,
            searchKeyword,
            sharedFilters,
            sortBy,
            sortOrder,
        ],
        queryFn: ({ signal, pageParam }) =>
            fetchPromoMetricData(
                planId,
                {
                    dimensions,
                    kpis,
                    kpi_view_type: kpiViewType,
                    metric_type: metricType,
                    time_period: timePeriod,
                    filters: sharedFilters ?? null,
                    search_string: searchKeyword,
                    sort_by: sortBy,
                    sorting_order: sortOrder,
                    limit: PAGE_LIMIT,
                    offset: pageParam,
                },
                signal,
            ),
        initialPageParam: 0,
        getNextPageParam: (lastPage, _pages, lastPageParam) => {
            const totalCount = lastPage?.metadata?.total_count ?? 0;
            const nextOffset = lastPageParam + PAGE_LIMIT;
            return nextOffset < totalCount ? nextOffset : undefined;
        },
        enabled,
    });

    const dataSource = useMemo<PromoMetricRow[]>(() => {
        if (!data?.pages?.length) return [];
        return data.pages.flatMap((page) => page.tableData ?? []);
    }, [data]);

    const totalCount = data?.pages?.[0]?.metadata?.total_count ?? 0;
    const loadedCount = dataSource.length;

    return {
        dataSource,
        isPending: enabled ? isPending : false,
        isFetching,
        isFetchingNextPage,
        hasNextPage: hasNextPage ?? false,
        fetchNextPage: () => fetchNextPage(),
        totalCount,
        loadedCount,
        error,
    };
};

export default usePromoMetricData;
